import { useCallback, useEffect, useRef, useState } from "react";
import { scanPath, useScanProject } from "components/ScanProjectRoute/ScanProjectRoute";
import { useNavigate, useLocation } from "react-router-dom";

//redux

// styles
import { StyledScanKitNew } from "./StyledScanKitNew";

// plugin
import Dropzone from "react-dropzone";

// icons
import { HiExclamationCircle } from "react-icons/hi";
import { ReactComponent as FileUpload } from "./images/Art-Save.svg";

// api
import { uploadFile } from "api/scankit/uploadFile.api";
import {
  createImageManifest,
  createImageBatchClient,
  uploadImageBatch,
  batchErrorMessage,
} from "api/scankit/imageBatches.api";

// plugin
import Cookies from "js-cookie";

// components
import ScanHeader from "components/ScanHeader/ScanHeader";
import ScanButton from "components/ScanButton/ScanButton";

// i18n
import { useTranslation, Trans } from "react-i18next";

const ScanKitNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search: queryString } = useLocation();
  const urlParams = new URLSearchParams(queryString);
  const isReupload = urlParams.get("reupload");

  const {
    identity: { projectId, modelId },
  } = useScanProject();
  const controllerRef = useRef(null);
  const manifestRef = useRef(null);
  const imageUploadStartedRef = useRef(false);
  const [fileData, setFileData] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState("");

  const runImages = useCallback(
    async (manifest) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const { signal } = controller;
      const client = createImageBatchClient({
        projectId,
        modelId,
        token: Cookies.get("token"),
        signal,
      });
      setUploading(true);
      setUploadProgress(0);
      setImageError("");
      try {
        imageUploadStartedRef.current = true;
        const batch = await uploadImageBatch({
          client,
          manifest,
          signal,
          onProgress: setUploadProgress,
        });
        if (!signal.aborted) {
          if (batch.originalsReady !== true) {
            throw new Error(batch.errorCode || "ORIGINALS_NOT_READY");
          }
          manifestRef.current = null;
          navigate(scanPath("info", projectId));
        }
      } catch (error) {
        if (!signal.aborted) setImageError(batchErrorMessage(error, t));
      } finally {
        if (!signal.aborted) setUploading(false);
      }
    },
    [projectId, modelId, navigate, t],
  );

  useEffect(() => () => controllerRef.current?.abort(), []);

  const handleCheckFileType = (acceptedFiles, rejectedFiles) => {
    if (uploading) return;
    setImageError("");
    setFileData(null);
    manifestRef.current = null;
    if (rejectedFiles.length || !acceptedFiles.length) {
      setImageError(t("scankitNew.batch.errors.FILE_TYPE"));
      return;
    }
    if (acceptedFiles[0].type === "video/mp4") {
      if (acceptedFiles.length !== 1 || acceptedFiles[0].size > 200 * 1024 * 1024) {
        setImageError(t("scankitNew.batch.errors.VIDEO_SIZE"));
        return;
      }
      setFileType("video");
    } else {
      try {
        manifestRef.current = createImageManifest(acceptedFiles);
      } catch (error) {
        setImageError(batchErrorMessage(error, t));
        return;
      }
      setFileType("images");
    }
    setFileData(acceptedFiles);
  };

  const handleUploadEvent = async () => {
    if (!fileData || uploading) return;
    if (fileType === "images") {
      await runImages(manifestRef.current);
      return;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    setUploading(true);
    const formData = new FormData();
    fileData.forEach((file) => formData.append("file", file));
    try {
      await uploadFile(
        formData,
        fileType,
        Cookies.get("token"),
        projectId,
        modelId,
        setUploadProgress,
        controller.signal,
      );
      if (!controller.signal.aborted) navigate(scanPath("info", projectId));
    } catch (error) {
      if (!controller.signal.aborted) setImageError(batchErrorMessage(error, t));
    } finally {
      if (!controller.signal.aborted) setUploading(false);
    }
  };

  const handleBackToHomeEvent = () => navigate("/cardList");

  const handleBackEvent = () => {
    navigate(imageUploadStartedRef.current || isReupload ? "/cardList" : "/chooseType");
  };

  return (
    <StyledScanKitNew uploadProgress={uploadProgress}>
      {uploading && (
        <div className="uploading">
          <div className="loadingWrapper">
            <div className={fileData && "fileNamePanel"}>
              <FileUpload />
              {fileData && (
                <Trans i18nKey="scankitNew.totalUpload">
                  共上傳{{ fileLength: fileData.length }}個檔案
                </Trans>
              )}
            </div>
            <div className="loadingBar"></div>
            <p className="title">{`${t("scankitNew.uploading")} ${uploadProgress}%`}</p>
          </div>
        </div>
      )}
      <ScanHeader
        title={Boolean(isReupload) ? t("scankitNew.Reupload") : undefined}
        clickEvent={handleBackToHomeEvent}
      />
      <div className="contentContainer">
        <div className="uploadContainer">
          <div className="uploadWrapper">
            {imageError && <p role="alert">{imageError}</p>}
            <Dropzone
              disabled={uploading}
              onDrop={handleCheckFileType}
              accept="image/jpeg, image/png, image/webp, video/mp4"
              // minSize={0}
              // maxSize={1048576}
            >
              {({ getRootProps, getInputProps, isDragActive }) => (
                <section className={`dropzone ${isDragActive && "dragActive"}`}>
                  <div {...getRootProps()}>
                    <input {...getInputProps({ "aria-label": t("uploadObject") })} />
                    <div className={fileData && "fileNamePanel"}>
                      <FileUpload />
                      {fileData && (
                        <Trans i18nKey="scankitNew.totalUpload">
                          共上傳{{ fileLength: fileData.length }}個檔案
                        </Trans>
                      )}
                    </div>
                    <div className="textWrapper">
                      <p className="title">{t("scankitNew.clickAndDragTheFile")}</p>
                      <ul className="description">
                        <li>
                          <HiExclamationCircle />
                          <div>
                            <Trans i18nKey="scankitNew.imageFormatSupport">
                              圖片格式僅支援<span>JPG、PNG、WebP</span>
                            </Trans>
                          </div>
                        </li>
                        <li>
                          <HiExclamationCircle />
                          <div>
                            <Trans i18nKey="scankitNew.theMaxSupport">
                              每張圖片<span>最大容量100MB</span>、圖片張數<span>最多150張</span>
                              <br />
                              (建議容量小於20MB、解析度720p、張數10張以內)
                            </Trans>
                          </div>
                        </li>
                        <li>
                          <HiExclamationCircle />
                          <div>
                            <Trans i18nKey="scankitNew.theRuleOfFileName">
                              圖片檔案命名規則只能使用<span>數字字元</span>並<span>依照順序</span>
                              排列
                              <br />
                              (範例: 001.png、002.png、003.png ...以此類推)
                            </Trans>
                          </div>
                        </li>
                        <li>
                          <HiExclamationCircle />
                          <div>{t("scankitNew.batch.videoFormat")}</div>
                        </li>
                        <li>
                          <HiExclamationCircle />
                          <div>{t("scankitNew.batch.videoLimit")}</div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>
              )}
            </Dropzone>
            <div className="buttonPanel">
              <ScanButton type="secondary" text={t("common.return")} clickEvent={handleBackEvent} />
              <ScanButton
                type="primary"
                text={t("uploadObject")}
                clickEvent={handleUploadEvent}
                disabled={!fileData || uploading}
              />
            </div>
          </div>
        </div>
      </div>
    </StyledScanKitNew>
  );
};

export default ScanKitNew;
